import { action, computed, observable } from 'mobx';
import {
  API_ACCOUNT_CANCEL,
  API_ACCOUNT_COPY,
  API_ACCOUNTS_SUMMARY,
  API_ACCOUNTS_DRAFTS,
  API_ACCOUNTS_ACTIVES,
  API_ACCOUNTS_INACTIVE,
  API_ACCOUNTS_DATES,
  API_ACCOUNTS_CSV,
  API_ACCOUNTS_REFRESH,
  API_ACCOUNTS_REFRESH_INACTIVE,
} from '../api/endpoints';
import { ACCOUNTS_EDIT } from 'routes';
import { SingleValueResponse } from 'common/interfaces';
import { RootStore } from './rootStore';
import { AmountValue } from 'common/interfaces';
import { AccountStatus, ActiveStatus, Order, ClientView } from '../enums';
import { DateTime } from 'luxon';
import { jsDateToLocaleString, sqlDateToLabel, compareChartDates } from 'helpers/helpers';
import { PendingChanges } from 'stores/account/model';
import { ViewKeys } from 'stores/userStoreKC';
import { Currency, AccountMode } from 'enums';
import history from 'helpers/routerHistory';

export enum DueSeverity {
  NORMAL = 'NORMAL',
  CLOSE = 'CLOSE',
  DUE = 'DUE',
  OVERDUE = 'OVERDUE',
  NONE = '',
}

export enum TwoFactorStatus {
  NONE = 'NONE',
  INCOMPLETE = 'INCOMPLETE',
  COMPLETE = 'COMPLETE',
  IRRELEVANT = 'IRRELEVANT',
}

export enum ClientAccess {
  SECOND_FACTOR_MISSING = 'SECOND_FACTOR_MISSING',
  ACCESS_NOT_ACTIVE = 'ACCESS_NOT_ACTIVE',
  BASIC_ACCESS = 'BASIC_ACCESS',
  FULL_ACCESS = 'FULL_ACCESS',
  SINGLE_FACTOR_ONLY = 'SINGLE_FACTOR_ONLY',
}

export const overdue = [DueSeverity.CLOSE, DueSeverity.DUE, DueSeverity.OVERDUE];

interface AccountsSummary {
  totalAum: AmountValue;
  totalAccounts: number;
  averageAum: AmountValue;
  totalPeriodic: AmountValue;
  conversionRate: number;
  driftThreshold: [number, number];
}

interface Client {
  companyName: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  name?: string;
}

export interface Portfolio {
  portfolio: string;
  variableSymbol: string;
  balance: AmountValue;
  rebalancing: boolean;
  currencyHedging: boolean;
  rebalancingTimeTest?: boolean;
  drift?: number;
}

interface Account {
  client: Client;
  accountId: string;
  title: string;
  lastUpdate: string;
  status: AccountStatus;
  pendingChanges: PendingChanges;
  currency: Currency;
  hasNotes: boolean;
  isProtected?: boolean;
}

interface AccountItem {
  clientName: string;
  birthDate: string;
  accountName: string;
  lastUpdate: string;
  status: string;
  accountId: string;
  currency: Currency;
  hasNotes: boolean;
}

interface Draft extends Account {
  initialDeposit: AmountValue;
  periodicDeposit: AmountValue;
  hasCommission: boolean;
  clientView: ClientView;
  twoFactor: TwoFactorStatus;
}

export interface DraftItem extends AccountItem {
  singleInvestment: number;
  regularInvestment: number;
  hasCommission: boolean;
  clientView: ClientView;
  twoFactor: TwoFactorStatus;
  isProtected: boolean;
}

interface Active extends Account {
  name: string;
  completedOn: string;
  processing: boolean;
  periodicDeposit: AmountValue;
  totalAum: AmountValue;
  totalProfit: AmountValue;
  yearlyProfit?: number;
  portfolioShort: Portfolio;
  portfolioMid: Portfolio;
  portfolioLong: Portfolio;
  portfolioWallet: {
    variableSymbol: string;
    balance: AmountValue;
  };
  clientAccess: ClientAccess;
  hasCommission: boolean;
  expectedDeposits: AmountValue;
  portfolioOnly: boolean;
  planMode: AccountMode;
  clientTaxMode: 'OBH' | 'PPP';
}

export interface ActiveItem extends AccountItem {
  completedOn: string;
  processing: boolean;
  regularInvestment: number;
  totalAum: number;
  totalProfit: number;
  yearlyProfit: number | null;
  portfolioShortBalance: number;
  portfolioMidBalance: number;
  portfolioLongBalance: number;
  portfolioWallet: number;
  clientAccess: ClientAccess;
  hasCommission: boolean;
  portfolioOnly: boolean;
  planMode: AccountMode;
  clientTaxMode: 'OBH' | 'PPP';
}

export interface AccountDate {
  date: string;
  severity: DueSeverity;
}

interface Dates extends Active {
  lastPaymentDate: string;
  lastPaymentValue: AmountValue;
  closestTargetDate: AccountDate;
  closestTargetValue: AmountValue;
  closestDepositDate: AccountDate;
  closestDepositValue: AmountValue;
  financialQuestionnaireValidityDate: AccountDate;
  investmentQuestionnaireValidityDate: AccountDate;
  fqOrIqValidityDate: AccountDate;
  expectedDeposits: AmountValue;
}

interface DatesItem extends ActiveItem {
  lastPaymentDate: string;
  lastPaymentValue: number;
  closestTargetDate: AccountDate;
  closestTargetReadable: string;
  closestTargetValue: number;
  closestTargetSeverity: DueSeverity;
  closestDepositDate: AccountDate;
  closestDepositReadable: string;
  closestDepositValue: number;
  closestDepositSeverity: DueSeverity;
  financialQuestionnaireValidityDate: string;
  financialQuestionnaireValiditySeverity: DueSeverity;
  investmentQuestionnaireValidityDate: string;
  investmentQuestionnaireValiditySeverity: DueSeverity;
  fqOrIqValidityDate: string;
  fqOrIqValiditySeverity: DueSeverity;
  expectedDeposits: number;
}

interface Inactive extends Account {
  canTerminateContract: boolean;
  lastPaymentDate: string;
  lastPaymentValue: AmountValue;
  totalAum: AmountValue;
  activeStatus: ActiveStatus;
}

interface InactiveItem extends AccountItem {
  canTerminateContract: boolean;
  lastPaymentDate: string;
  lastPaymentValue: number;
  totalAum: number;
  activeStatus: string;
}

export enum AccountPageTypes {
  ACTIVE = 'ACTIVE',
  DRAFTS = 'DRAFTS',
  DATES = 'DATES',
  INACTIVE = 'INACTIVE',
}

const PAGE_SIZE_KEY = 'accountsPageSize';
const ACCOUNTS_STATE = 'accountsState';

interface ColState {
  name: string;
  hidden: boolean;
  hiddenByUser: boolean;
}

interface AccountsTableState {
  search: string;
  orderBy?: number;
  order?: Order;
  userModified?: boolean;
  cols: ColState[];
}

const getAccountDefaultState = () => {
  let defaultState = localStorage.getItem(ACCOUNTS_STATE) && JSON.parse(localStorage.getItem(ACCOUNTS_STATE)!);

  if (!defaultState || Object.keys(AccountPageTypes).some((type) => !Object.keys(defaultState).includes(type))) {
    defaultState = Object.keys(AccountPageTypes).reduce((prev: any, current) => {
      prev[current] = defaultState && defaultState[current] ? defaultState[current] : { search: '' };
      return prev;
    }, {});
    localStorage.setItem(ACCOUNTS_STATE, JSON.stringify(defaultState));
  }
  return defaultState;
};

export class AccountsStore {
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  @observable summary?: AccountsSummary;

  @observable draftsResponse?: Draft[] = undefined;
  @observable activesResponse?: Active[] = undefined;
  @observable datesResponse?: Dates[] = undefined;
  @observable inactiveResponse?: Inactive[] = undefined;
  @observable pageSize = parseInt(localStorage.getItem(PAGE_SIZE_KEY) || '20');

  @observable generationDialog = false;
  @observable generationDate?: string = undefined;
  @observable generationDrive = false;
  @observable activeCommissions = false;
  @observable draftCommissions = false;

  @observable reloadingActive = false;
  @observable reloadingInactive = false;

  @observable accountsState: {
    [key: string]: AccountsTableState;
  } = getAccountDefaultState();

  @action hideGenerationDialog = () => (this.generationDialog = false);

  @action reloadAccounts = async (type: 'ACTIVE' | 'INACTIVE') => {
    let ep = null;
    switch (type) {
      case 'ACTIVE':
        ep = API_ACCOUNTS_REFRESH;
        this.reloadingActive = true;
        break;
      case 'INACTIVE':
        ep = API_ACCOUNTS_REFRESH_INACTIVE;
        this.reloadingInactive = true;
        break;
    }

    const response = await this.rootStore.stores.apiStore.post(ep);

    if (response?.status !== 200) return;

    if (type === 'ACTIVE') {
      this.activesResponse = undefined;
      this.rootStore.stores.apiStore.removeFromCache(API_ACCOUNTS_ACTIVES);
      await this.fetchActives();
      this.reloadingActive = false;
    } else if (type === 'INACTIVE') {
      this.activesResponse = undefined;
      this.rootStore.stores.apiStore.removeFromCache(API_ACCOUNTS_INACTIVE);
      await this.fetchInactive();
      this.reloadingInactive = false;
    }

  };

  @action
  fetchSummary = async () => {
    const { apiStore } = this.rootStore.stores;
    const response = await apiStore.get<AccountsSummary>(API_ACCOUNTS_SUMMARY);
    if (response && response.data) {
      this.summary = response.data;
    }
  };

  @action
  fetchDrafts = async () => {
    const { apiStore } = this.rootStore.stores;
    const response = await apiStore.get<Draft[]>(API_ACCOUNTS_DRAFTS);
    if (response && response.data) {
      this.draftsResponse = response.data;
      if (this.draftsResponse.find((a) => a.hasCommission)) this.draftCommissions = true;
    }
  };

  @action
  fetchActives = async () => {
    const { apiStore } = this.rootStore.stores;
    const response = await apiStore.get<Active[]>(API_ACCOUNTS_ACTIVES);
    if (response && response.data) {
      this.activesResponse = response.data;
      if (this.activesResponse.find((a) => a.hasCommission)) {
        this.activeCommissions = true;
        this.accountsState[AccountPageTypes.ACTIVE].cols &&
          this.onChangeColumnHidden(AccountPageTypes.ACTIVE, 'commissions', false, false);
      }
    }
  };

  @action
  fetchDates = async () => {
    const { apiStore } = this.rootStore.stores;
    const response = await apiStore.get<Dates[]>(API_ACCOUNTS_DATES);
    if (response && response.data) {
      this.datesResponse = response.data;
    }
  };

  @action
  fetchInactive = async () => {
    const { apiStore } = this.rootStore.stores;
    const response = await apiStore.get<Inactive[]>(API_ACCOUNTS_INACTIVE);

    if (response && response.data) {
      this.inactiveResponse = response.data;
    }
  };

  transformBasicAccountData = (data: Account) => {
    return {
      clientName: data.client
        ? data.client.companyName
          ? data.client.companyName
          : data.client.firstName && data.client.lastName
          ? `${data.client.lastName} ${data.client.firstName}`
          : ''
        : '',
      birthDate: data.client ? `${jsDateToLocaleString(DateTime.fromSQL(data.client.birthDate).toJSDate())}` : '',
      accountName: data.title ? (data.title.length < 100 ? data.title : `${data.title.substring(0, 100)}…`) : '',
      lastUpdate: data.lastUpdate ? jsDateToLocaleString(DateTime.fromSQL(data.lastUpdate).toJSDate()) : '',
      status: data.status,
      accountId: data.accountId,
      id: data.accountId, // https://github.com/material-table-core/core/issues/391
      currency: data.currency,
      hasNotes: data.hasNotes,
      isProtected: !!data.isProtected
    };
  };

  transformActiveAccountData = (data: Active) => {
    return {
      clientName: data.client.name || '',
      completedOn: data.completedOn ? jsDateToLocaleString(DateTime.fromSQL(data.completedOn).toJSDate()) : '',
      processing: data.processing,
      regularInvestment: data.periodicDeposit ? data.periodicDeposit.value : 0,
      totalAum: data.totalAum ? data.totalAum.value : 0,
      yearlyProfit: data.yearlyProfit !== undefined ? data.yearlyProfit : null,
      totalProfit: data.totalProfit ? data.totalProfit.value : 0,
      portfolioShortBalance: data.portfolioShort?.balance ? data.portfolioShort.balance.value : 0,
      portfolioMidBalance: data.portfolioMid?.balance ? data.portfolioMid.balance.value : 0,
      portfolioLongBalance: data.portfolioLong?.balance ? data.portfolioLong.balance.value : 0,
      portfolioWallet: data.portfolioWallet?.balance ? data.portfolioWallet?.balance.value : 0,
      portfolioLong: data.portfolioLong,
      portfolioMid: data.portfolioMid,
      portfolioShort: data.portfolioShort,
      clientAccess: data.clientAccess,
      hasCommission: data.hasCommission,
      expectedDeposits: data.expectedDeposits ? data.expectedDeposits.value : 0,
      portfolioOnly: data.portfolioOnly,
      planMode: data.planMode,
      clientTaxMode: data.clientTaxMode
    };
  };

  @computed
  get drafts(): DraftItem[] | null {
    return this.draftsResponse
      ? this.draftsResponse.map((draft: Draft) => {
          const basicPart = this.transformBasicAccountData(draft);
          const draftPart = {
            singleInvestment: draft.initialDeposit ? draft.initialDeposit.value : 0,
            regularInvestment: draft.periodicDeposit ? draft.periodicDeposit.value : 0,
            hasCommission: draft.hasCommission,
            clientView: draft.clientView,
            twoFactor: draft.twoFactor,
          };
          return { ...basicPart, ...draftPart };
        })
      : null;
  }

  @computed
  get actives(): ActiveItem[] | null {
    return this.activesResponse
      ? this.activesResponse.map((active: Active) => {
          const basicPart = this.transformBasicAccountData(active);
          const activePart = this.transformActiveAccountData(active);
          return { ...basicPart, ...activePart };
        })
      : null;
  }

  @computed
  get dates(): DatesItem[] | null {
    return this.datesResponse
      ? this.datesResponse.map((dates: Dates) => {
          const basicPart = this.transformBasicAccountData(dates);
          const activePart = this.transformActiveAccountData(dates);
          const fqOrIq =
            compareChartDates(
              dates.financialQuestionnaireValidityDate.date,
              dates.investmentQuestionnaireValidityDate.date,
            ) < 0
              ? dates.financialQuestionnaireValidityDate
              : dates.investmentQuestionnaireValidityDate;

          const datesPart = {
            lastPaymentDate: dates.lastPaymentDate
              ? jsDateToLocaleString(DateTime.fromSQL(dates.lastPaymentDate).toJSDate())
              : '',
            lastPaymentValue: dates.lastPaymentValue ? dates.lastPaymentValue.value : 0,
            closestTargetReadable: dates.closestTargetDate
              ? sqlDateToLabel(dates.closestTargetDate.date, 'LLLL yyyy')
              : '',
            closestTargetDate: dates.closestTargetDate,
            closestTargetValue: dates.closestTargetValue ? dates.closestTargetValue.value : 0,
            closestTargetSeverity: dates.closestTargetDate ? dates.closestTargetDate.severity : DueSeverity.NONE,
            closestDepositReadable: dates.closestDepositDate
              ? sqlDateToLabel(dates.closestDepositDate.date, 'LLLL yyyy')
              : '',
            closestDepositDate: dates.closestDepositDate,
            closestDepositValue: dates.closestDepositValue ? dates.closestDepositValue.value : 0,
            closestDepositSeverity: dates.closestDepositDate ? dates.closestDepositDate.severity : DueSeverity.NONE,
            financialQuestionnaireValidityDate: dates.financialQuestionnaireValidityDate
              ? jsDateToLocaleString(DateTime.fromSQL(dates.financialQuestionnaireValidityDate.date).toJSDate())
              : '',
            financialQuestionnaireValiditySeverity: dates.financialQuestionnaireValidityDate
              ? dates.financialQuestionnaireValidityDate.severity
              : DueSeverity.NONE,
            investmentQuestionnaireValidityDate: dates.investmentQuestionnaireValidityDate
              ? jsDateToLocaleString(DateTime.fromSQL(dates.investmentQuestionnaireValidityDate.date).toJSDate())
              : '',
            investmentQuestionnaireValiditySeverity: dates.investmentQuestionnaireValidityDate
              ? dates.investmentQuestionnaireValidityDate.severity
              : DueSeverity.NONE,
            fqOrIqValidityDate: fqOrIq ? jsDateToLocaleString(DateTime.fromSQL(fqOrIq.date).toJSDate()) : '',
            fqOrIqValiditySeverity: fqOrIq ? fqOrIq.severity : DueSeverity.NONE,

            clientName: dates.client
              ? dates.client.companyName
                ? dates.client.companyName
                : dates.client.firstName && dates.client.lastName
                ? `${dates.client.lastName} ${dates.client.firstName}`
                : ''
              : '',
          };

          return { ...basicPart, ...activePart, ...datesPart };
        })
      : null;
  }

  @computed
  get inactive(): InactiveItem[] | null {
    return this.inactiveResponse
      ? this.inactiveResponse.map((inactive: Inactive) => {
          const basicPart = this.transformBasicAccountData(inactive);
          const inactivePart = {
            canTerminateContract: inactive.canTerminateContract,
            lastPaymentDate: inactive.lastPaymentDate
              ? jsDateToLocaleString(DateTime.fromSQL(inactive.lastPaymentDate).toJSDate())
              : '',
            lastPaymentValue: inactive.lastPaymentValue ? inactive.lastPaymentValue.value : 0,
            totalAum: inactive.totalAum ? inactive.totalAum.value : 0,
            activeStatus: inactive.activeStatus || '',
          };
          return { ...basicPart, ...inactivePart };
        })
      : null;
  }

  @computed
  get questionnaireOverdue(): boolean {
    return this.datesResponse
      ? this.datesResponse.some(
          (date) =>
            overdue.includes(date.financialQuestionnaireValidityDate.severity) ||
            overdue.includes(date.investmentQuestionnaireValidityDate.severity),
        )
      : false;
  }

  @action cancelAccount = async (accountId: string) => {
    const { apiStore, uiStore } = this.rootStore.stores;
    uiStore.setLoading(true);
    const response = await apiStore.patch<{ data: any }>(API_ACCOUNT_CANCEL(accountId));
    uiStore.setLoading(false);

    if (response && response.data) {
      this.fetchDrafts();
    }
  };

  @action onChangeRowsPerPage = (pageSize: number) => {
    this.pageSize = pageSize;
    localStorage.setItem(PAGE_SIZE_KEY, JSON.stringify(pageSize));
  };

  @action onTableSearchChange = (type: AccountPageTypes, search: string) => {
    const state = this.accountsState;
    const typeState = state[type];

    typeState.search = search;
    this.accountsState = state;

    localStorage.setItem(ACCOUNTS_STATE, JSON.stringify(state));
  };

  @action onTableOrderChange = (type: AccountPageTypes, orderedColumnId: number, orderDirection: Order) => {
    const state = this.accountsState;
    const typeState = state[type];

    typeState.orderBy = orderedColumnId;
    typeState.order = orderDirection;

    this.accountsState = state;
    localStorage.setItem(ACCOUNTS_STATE, JSON.stringify(state));
  };

  @action onColumnDragged = (type: AccountPageTypes, sourceIndex: number, destinationIndex: number) => {
    const state = this.accountsState;
    const typeState = state[type];

    const currentCols = typeState.cols.filter((c) => !c.hidden);
    const source = typeState.cols.findIndex((c) => c.name === currentCols[sourceIndex].name);
    const destination = typeState.cols.findIndex((c) => c.name === currentCols[destinationIndex].name);
    typeState.cols.splice(destination, 0, typeState.cols.splice(source, 1)[0]);
    typeState.userModified = true;

    this.accountsState = state;
    localStorage.setItem(ACCOUNTS_STATE, JSON.stringify(state));
  };

  @action onChangeColumnHidden = (
    type: AccountPageTypes,
    column: string | number | symbol | undefined,
    hidden: boolean,
    user: boolean,
  ) => {
    const state = this.accountsState;
    const typeState = state[type];

    const index = typeState.cols.findIndex((c) => c.name === column);
    if (index !== -1) {
      typeState.cols[index].hidden = hidden;
      if (user) typeState.cols[index].hiddenByUser = hidden;
    }
    if (user) typeState.userModified = true;
    this.accountsState = state;
    localStorage.setItem(ACCOUNTS_STATE, JSON.stringify(state));
  };

  @action
  resetColumns = (type: AccountPageTypes) => {
    const state = this.accountsState;
    const typeState = state[type];
    typeState.cols = [];
    typeState.userModified = false;
    this.accountsState = state;
    localStorage.setItem(ACCOUNTS_STATE, JSON.stringify(state));
  };

  @action
  downloadAccounts = async () => {
    const { post } = this.rootStore.stores.apiStore;
    const { uiStore } = this.rootStore.stores;
    uiStore.setLoading(true);
    const response = await post<{ value: string }>(API_ACCOUNTS_CSV);
    if (response?.data) {
      this.generationDate = response.data.value;
      this.generationDialog = true;
    }
    uiStore.setLoading(false);
  };

  @action
  updateNoteInList = (
    accountId: string,
    hasNote: boolean,
    response?: Active[] | Dates[] | Draft[],
    cacheKey?: string,
  ) => {
    if (response) {
      const findIndex = response.findIndex((a) => a.accountId === accountId);
      if (findIndex !== -1) {
        const temp = response;
        temp[findIndex].hasNotes = hasNote;
        response = temp;
        if (cacheKey) {
          const cache = sessionStorage.getItem(cacheKey);
          if (cache) {
            const parsed = JSON.parse(cache);
            parsed.response.data = response;
            sessionStorage.setItem(cacheKey, JSON.stringify(parsed));
          }
        }
      }
    }
  };

  @action
  updateNoteStatus = (tab: AccountPageTypes, accountId: string, hasNote: boolean) => {
    switch (tab) {
      case AccountPageTypes.ACTIVE:
      case AccountPageTypes.DATES:
        this.updateNoteInList(accountId, hasNote, this.activesResponse, API_ACCOUNTS_ACTIVES);
        this.updateNoteInList(accountId, hasNote, this.datesResponse, API_ACCOUNTS_DATES);
        break;
      case AccountPageTypes.DRAFTS:
        this.updateNoteInList(accountId, hasNote, this.draftsResponse);
        break;
    }
  };

  sortColumns = <T extends any>(
    updateSavedSetting: boolean,
    type: AccountPageTypes,
    columns: T[],
    defaultOrder: Order,
    defaultOrderBy: number,
  ): T[] => {
    const state = this.accountsState;
    const typeState = state[type];
    let { order, orderBy } = typeState;
    if (!order) order = defaultOrder;
    if (orderBy === undefined) orderBy = defaultOrderBy;

    if (!Array.isArray(columns)) return columns;

    const demo = this.rootStore.stores.userStore.view[ViewKeys.DEMO_MODE];

    const generateColOrder = !typeState.cols || typeState.cols.length === 0;
    if (generateColOrder) {
      typeState.cols = [];
    } else {
      this.onChangeColumnHidden(type, 'clientName', demo === true, false);
    }

    let updateColumns = false;

    // FIXME: get type from material-table
    const sortedColumns = columns
      .map((column: any, index: number) => {
        if (generateColOrder) {
          typeState.cols.push({
            name: column.field,
            hidden: demo && column.field === 'clientName' ? true : column.hidden || false,
            hiddenByUser: false,
          });
        }

        if (typeState.cols.findIndex((c) => c.name === column.field) === -1) {
          typeState.cols.splice(index, 0, {
            name: column.field,
            hidden: demo && column.field === 'clientName' ? true : column.hidden || false,
            hiddenByUser: false,
          });
          updateColumns = true;
        }

        if (updateSavedSetting && !generateColOrder && !typeState.userModified) {
          typeState.cols[typeState.cols.findIndex((c) => c.name === column.field)].hidden =
            demo && column.field === 'clientName' ? true : column.hidden || false;
        }

        return {
          ...column,
          defaultSort: index === orderBy ? order : undefined,
          hidden:
            column.hidden ||
            typeState.cols.find((c) => c.name === column.field)?.hidden === true ||
            typeState.cols.find((c) => c.name === column.field)?.hiddenByUser === true,
          hiddenByColumnsButton: demo && column.field === 'clientName' ? true : column.hiddenByColumnsButton,
        };
      })
      .sort(
        (a, b) =>
          typeState.cols.findIndex((c) => c.name === a.field) - typeState.cols.findIndex((c) => c.name === b.field),
      );

    if (generateColOrder || updateColumns) {
      state[type] = typeState;
      this.accountsState = state;
      localStorage.setItem(ACCOUNTS_STATE, JSON.stringify(state));
    }
    return sortedColumns;
  };

  copyAccount = async(accountId: string) => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.post<SingleValueResponse<string>>(API_ACCOUNT_COPY(accountId));
    if (response?.data) {
      history.push(ACCOUNTS_EDIT(response.data.value));
    }
    this.rootStore.stores.uiStore.setLoading(false);
  }

}
